/**
 * Moving rows from the keyboard, decided purely: a direction in, the same
 * `Move`s a drag and drop would make out — so a keyboard move and a drop
 * share one set of parent rules, one `ord` computation and one undo entry
 * (`drop.ts`'s `planDrop` does the work; this module only picks the target).
 *
 *     up / down            one slot among the rows of the same kind under the
 *                          same parent; at the first / last slot, into the
 *                          previous parent (last) / next parent (first)
 *     prev-parent / next-parent
 *                          straight into the previous parent (last) / the
 *                          next parent (first)
 *
 * "Parent" means the rows of the parent's kind in running order across the
 * whole rundown (every part of every segment for a piece), collapsed or not.
 * The hierarchy is fixed (`ALLOWED_PARENT_KINDS`: a part is always under a
 * segment), so there is no indent / outdent that keeps a row's kind; moving
 * into the neighbouring parent is what "in" and "out" can mean here.
 * A segment and a global adlib live directly under the rundown and only move
 * up and down.
 */
import type { RowKind } from '@norna/domain/fields.js';
import type { Row } from '@norna/domain/row.js';
import { childrenOf, type RowTree } from '@norna/domain/tree.js';
import { childrenOfKind, type DropTarget, orderedBlock, planDrop } from './drop.js';
import type { Move } from './patches.js';

export type MoveDirection = 'up' | 'down' | 'prev-parent' | 'next-parent';

/** Every row of `kind` under `rootId`, depth first in `ord` order (the running order). */
export function rowsOfKindInOrder(tree: RowTree, rootId: string, kind: RowKind): Row[] {
  const out: Row[] = [];
  const seen = new Set<string>();
  const walk = (id: string): void => {
    for (const child of childrenOf(tree, id)) {
      if (seen.has(child.rowId)) continue;
      seen.add(child.rowId);
      if (child.kind === kind) out.push(child);
      walk(child.rowId);
    }
  };
  walk(rootId);
  return out;
}

/** The previous or next row of the parent's kind in running order, if the parent is one the rundown holds. */
function neighbourParent(tree: RowTree, rundownId: string, parentId: string, step: -1 | 1): Row | null {
  if (parentId === rundownId) return null;
  const parent = tree.rows.get(parentId);
  if (parent === undefined) return null;
  const parents = rowsOfKindInOrder(tree, rundownId, parent.kind);
  const index = parents.findIndex((r) => r.rowId === parentId);
  if (index < 0) return null;
  return parents[index + step] ?? null;
}

/** The nearest same-kind sibling of `anchorId` in `step`'s direction that is not being moved. */
function neighbourSibling(tree: RowTree, anchor: Row, parentId: string, moving: ReadonlySet<string>, step: -1 | 1): Row | null {
  const siblings = childrenOfKind(tree, parentId, anchor.kind);
  let i = siblings.findIndex((r) => r.rowId === anchor.rowId) + step;
  while (i >= 0 && i < siblings.length) {
    const candidate = siblings[i];
    if (candidate !== undefined && !moving.has(candidate.rowId)) return candidate;
    i += step;
  }
  return null;
}

/** The row a move is measured from: the first of the block going up, the last going down. */
function anchorOf(tree: RowTree, ids: readonly string[], up: boolean): Row | null {
  const anchorId = up ? ids[0] : ids.at(-1);
  return (anchorId === undefined ? undefined : tree.rows.get(anchorId)) ?? null;
}

/** Where a keyboard move of the block drops it, as a drop target; null when there is nowhere to go. */
export function keyboardTarget(tree: RowTree, rundownId: string, ids: readonly string[], direction: MoveDirection): DropTarget | null {
  const up = direction === 'up' || direction === 'prev-parent';
  const step = up ? -1 : 1;
  const anchor = anchorOf(tree, ids, up);
  const parentId = anchor?.parentId ?? null;
  if (anchor === null || parentId === null) return null;
  const oneSlot = direction === 'up' || direction === 'down';
  const sibling = oneSlot ? neighbourSibling(tree, anchor, parentId, new Set(ids), step) : null;
  if (sibling !== null) return { overId: sibling.rowId, position: up ? 'before' : 'after' };
  const parent = neighbourParent(tree, rundownId, parentId, step);
  // Into the previous parent at its end ('inside'), into the next at its start ('before').
  return parent === null ? null : { overId: parent.rowId, position: up ? 'inside' : 'before' };
}

/**
 * The moves for a keyboard move of `ids` (the selection, in any order) in
 * `direction`: null when the rows cannot move that way (the first segment
 * up, a mixed-kind selection, the last part of the last segment down).
 */
export function planKeyboardMove(tree: RowTree, rundownId: string, displayOrder: readonly string[], ids: readonly string[], direction: MoveDirection, writerTag: string): readonly Move[] | null {
  const block = orderedBlock(tree, displayOrder, ids);
  if (block === null) return null;
  const target = keyboardTarget(tree, rundownId, block.ids, direction);
  return target === null ? null : planDrop(tree, displayOrder, block.ids, target, writerTag);
}

function nameOf(row: Row): string {
  const name = row.kind === 'part' ? row.f['title'] : row.f['name'];
  return typeof name === 'string' && name !== '' ? name : `untitled ${row.kind}`;
}

/**
 * What a screen reader hears after a move (the tree's live region), read
 * from the tree after the move was applied: "Moved “Weather” to position 2
 * of 3 in “Headlines”."
 */
export function moveAnnouncement(tree: RowTree, ids: readonly string[]): string {
  const first = ids[0] === undefined ? undefined : tree.rows.get(ids[0]);
  const parentId = first?.parentId ?? null;
  if (first === undefined || parentId === null) return '';
  const siblings = childrenOfKind(tree, parentId, first.kind);
  const position = siblings.findIndex((r) => r.rowId === first.rowId) + 1;
  const parent = tree.rows.get(parentId);
  const what = ids.length === 1 ? `“${nameOf(first)}”` : `${String(ids.length)} ${first.kind}s`;
  const where = parent === undefined || parent.kind === 'rundown' ? 'the rundown' : `“${nameOf(parent)}”`;
  return `Moved ${what} to position ${String(position)} of ${String(siblings.length)} in ${where}.`;
}
